import React, { useRef, useState } from 'react';
import { Box, Stack, Typography, Button, Divider } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { ActionMenu } from 'components/UI/ActionMenu';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { GiftCertificate } from 'components/pages/certificates/productPage/GiftCertificate';
import { ReportMissingDrawer } from 'components/pages/certificates/productPage/ReportMissingDrawer';
import { buildCertificateActionItems } from 'components/pages/certificates/certificateActionItems';
import { getGiftAvailability, getReportMissingAvailability } from 'utils/certificateActions';
import { border, font } from 'settings/tokens';
import { innerRadius, surfaceRadius } from 'settings/brandRadius';
import { SelectCheckbox } from 'components/UI/SelectCheckbox';
import type { CertificateCustomer } from 'types';

export type SelectionToolbarProps = {
  certificates?: any[];
  selectedIds: string[];
  onSelectAll: () => void;
  onClearSelection: () => void;
  /**
   * No longer called from inside the bar — Reset clears rather than closes, and
   * the toolbar's Select toggle is the close affordance. Kept so the call site
   * still owns the open/closed state in one place.
   */
  onClose?: () => void;
  /** Drop specific ids from the selection — powers "Deselect them". */
  onDeselectIds?: (ids: string[]) => void;
  /**
   * A bulk action changed the records the list is showing — refetch it.
   *
   * Only the REPORT path needs this. The gift flow invalidates
   * `['certificates']` itself once the user dismisses its confirmation (see
   * `GiftCertificate.flushCertificateQueries`, and `useInitTransfer` for why the
   * invalidation is deferred to dismissal rather than fired on success).
   */
  onActionComplete?: () => void;
};

export const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
  certificates,
  selectedIds,
  onSelectAll,
  onClearSelection,
  onDeselectIds,
  onActionComplete,
}) => {
  const [openGift, setOpenGift] = useState(false);
  const [openReportMissing, setOpenReportMissing] = useState(false);
  /*
   * The gift's target is LATCHED when the row is pressed, not derived from the
   * live selection on every render.
   *
   * Deriving it looks tidier and breaks the close: a sent gift clears the
   * selection (the certificate is now mid-transfer and would otherwise sit in the
   * bar blocking everything), which in the derived form takes the target to
   * `undefined` in the same batch that closes the sheet — so the `{target && …}`
   * guard unmounts the drawer outright and MUI's exit Slide has nothing left to
   * animate. That is the identical failure the `NOT wrapped in open && (...)`
   * notes on this page's other drawers document.
   *
   * Latching also means the sheet cannot change which certificate it is gifting
   * underneath a user who is halfway through picking a recipient.
   */
  const [giftTarget, setGiftTarget] = useState<CertificateCustomer | null>(null);
  /** Did the report drawer change anything before it was dismissed? See its `onClose`. */
  const reportedRef = useRef(false);

  // Actions follow the SELECTION, not the page. See `utils/certificateActions`
  // for why an action refuses outright rather than acting on the eligible subset.
  const selectedCertificates = (certificates ?? []).filter((c: any) =>
    selectedIds.includes(c?.id),
  ) as CertificateCustomer[];

  /*
   * PER-ACTION, not one shared verdict.
   *
   * They agree today — both need `isIdleOwned` — and it would be a smaller diff
   * to compute one `availability` and apply it to both rows. That is the shape
   * that produced the defect this change fixes: a bar sized to exactly one
   * action, which then could not grow. Gift already carries a constraint Report
   * does not (one at a time — a gift ends in a physical tag scan), so the two
   * verdicts are ALREADY different, and Sell will differ again.
   */
  const gift = getGiftAvailability(selectedCertificates);
  const reportMissing = getReportMissingAvailability(selectedCertificates);

  /*
   * §46 — and a deliberate, argued departure from its default for this surface.
   *
   * §46 says an action unavailable because of RECORD STATE should HIDE, because
   * resolution belongs to the user and the app owes them the resolving action
   * rather than a tombstone. That is right for a row kebab, where the record is
   * fixed and the menu's shape is stable. It is wrong here, for two reasons the
   * rule's own reasoning supplies:
   *
   *   1. The blocking state is not a property of A RECORD, it is a property of
   *      the user's LIVE INPUT. The selection changes on every tick, so hiding
   *      would make the menu's contents appear and disappear underneath the
   *      cursor as the user selects — the churn §46 explicitly names as the cost
   *      of getting this axis wrong, just from the other direction.
   *   2. Hiding is only better than a disabled row when the resolving action is
   *      reachable INSTEAD. Here it is reachable ANYWAY — "Deselect those N" is
   *      in the bar, one tap away, and it is a better resolver than anything a
   *      hidden row could point at. So hiding would cost the explanation and buy
   *      nothing.
   *
   * And the failure mode of hiding here is concrete: select one lost certificate
   * and both real actions vanish, leaving a bar whose menu holds a single
   * greyed-out "Sell — Coming soon" and no statement of why. That reads as
   * broken, which is the exact impression this change exists to remove.
   *
   * So selection-state blocks are DISABLED with the reason on the row, and §46's
   * forcing function is honoured rather than dodged: every disabled row here
   * carries a sentence saying why, in a `description` that a phone can read
   * (a tooltip cannot be reached on touch).
   */
  const actionItems = buildCertificateActionItems({
    gift: {
      onClick: () => {
        // Safe to index: `getGiftAvailability` only enables the row at exactly
        // one selected certificate, and a disabled row never reaches `onClick`
        // (`ActionMenu.handleItemClick` returns early).
        setGiftTarget(selectedCertificates[0] ?? null);
        setOpenGift(true);
      },
      disabled: !gift.enabled,
      description: gift.enabled ? undefined : gift.reason,
    },
    // No state passed for `sell` would omit it; passing an empty state includes
    // it, and the shared definition supplies the disabled + "Coming soon"
    // treatment so this call site cannot get it half-right.
    sell: {},
    reportMissing: {
      onClick: () => setOpenReportMissing(true),
      disabled: !reportMissing.enabled,
      description: reportMissing.enabled ? undefined : reportMissing.reason,
    },
  });

  const isAllSelected = Boolean(certificates?.length) && selectedIds.length === (certificates?.length ?? 0);
  const isIndeterminate = Boolean(selectedIds.length) && selectedIds.length < (certificates?.length ?? 0);

  const handleSelectAll = () => {
    if (!certificates) return;
    if (selectedIds.length > 0) {
      onClearSelection();
    } else {
      onSelectAll();
    }
  };

  /*
   * Reset CLEARS the selection and leaves the bar open, which is what the word
   * honestly means. It replaced a bare X, whose consequence was unreadable: an X
   * on a panel means dismiss, but this panel's X deliberately did NOT clear (see
   * handleClose), so the glyph promised one thing and did another.
   *
   * Losing the X does not strand anyone in select mode — the toolbar's Select
   * control is a toggle and closes the bar, which is also the control they used
   * to open it.
   */
  const hasSelection = selectedIds.length > 0;

  /*
   * The rows blocking ANY action, as a union.
   *
   * A union rather than one action's list because the resolver is a SELECTION
   * edit, not an action: dropping the rows that block Gift while leaving the ones
   * that block Report would hand back a selection that is still stuck, and the
   * user would have to press it again. Today the two sets are identical; the
   * union is what keeps this correct when they stop being.
   */
  const blockedIds = Array.from(new Set([...gift.blockedIds, ...reportMissing.blockedIds]));

  /*
   * The deselect-blocked link is shown ONLY when it differs from Reset.
   *
   * It survives as a distinct action because it usually keeps the actionable
   * remainder selected and turns the menu back on, which Reset cannot do. But
   * when EVERY selected item is blocked, "deselect the blocked ones" and "clear
   * the selection" are the same operation with two labels — and that is a real
   * state, hit immediately by selecting one certificate that happens to be
   * mid-transfer, where the panel offered two controls that both emptied the
   * selection.
   *
   * So the test is not "are any blocked" but "would anything be LEFT". If not,
   * Reset already says it, one control instead of two.
   */
  const showsDeselectBlocked = blockedIds.length > 0 && blockedIds.length < selectedIds.length;

  return (
    <Box
      sx={{
        // Sits INSIDE the sticky header now, so the bottom margin belongs to the
        // header's own rhythm rather than to a floating card in the page body.
        mb: '16px',
        border: '1px solid #E4E4E7',
        // Matches the cards below it. This was `borderRadius: 2`, which multiplied
        // the theme's shape radius at a factor nobody chose — on a brand with
        // border_radius 10 the bar rounded to 20px against the cards' 12px — and
        // was then pinned to a literal 12px, which stopped tracking the brand at
        // all. VRG-562: it now takes the SAME resolved step the cards take, so the
        // two cannot diverge in either direction.
        borderRadius: surfaceRadius,
        backgroundColor: theme => theme.palette.grey[50],
        overflow: 'hidden',
        px: { xs: 1, md: 3 },
        /*
          DESKTOP HEIGHT MATCHES THE ACTION LINE ABOVE IT (48px).
          This bar sits directly under the search + Filter/Sort/Layout row, and a
          16px `py` around 44px of content made it 76px — half again as tall as
          the row it hangs off, which read as a heavier, unrelated block rather
          than part of the same toolbar.

          48 is not a magic number: it is `toolbarControlSx.height.md` from
          `components/UI/fieldMetrics`, the same token every control in that row
          takes. If the toolbar band moves, this follows it.

          `minHeight`, not `height`, so the bar can still grow if its content ever
          exceeds one line rather than clipping.

          ⚠️ The `display: flex` + `justifyContent: space-between` that used to sit
          here is GONE, and the min-height moved to the row below. The bar now has
          a second, optional row (the deselect resolver), and a flex container
          justifying two rows apart pushed that resolver to the far right of the
          bar at md+ instead of under the controls it belongs to.
        */
        py: { xs: 1, md: 0 },
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        spacing={0}
        sx={{ width: '100%', minHeight: { md: 48 } }}
      >
        <Stack direction='row' alignItems='center' spacing={2}>
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <SelectCheckbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onChange={handleSelectAll}
              sx={{ ml: 0.5 }}
            />
            <Typography sx={{ fontSize: 16, color: theme => theme.palette.text.secondary, fontWeight: { xs: 400, md: 500 } }}>
              {/* Kept at BOTH widths. Hiding the words on a phone was a guess at
                  the fit and it was wrong — measured at 375 with all three
                  controls present, the row ends at x=350 inside a 359px bar, so
                  there is ~116px of free space between the Actions trigger and
                  Reset. A bare "(2)" also says nothing about what the number
                  counts. */}
              Select all ({selectedIds?.length})
            </Typography>
          </Stack>

          <Divider
            orientation='vertical'
            sx={{ height: 20, borderColor: border.subtle, display: { xs: 'none', md: 'block' } }}
          />

          {/*
            ONE trigger for the whole set, at BOTH widths, rather than a row of
            inline buttons.

            The bar used to render its single action inline, which does not
            survive the set growing: three actions, each able to carry a
            two-line reason, is ~230px of bar on a 375px phone — the list the
            bar operates on would be pushed off the fold by the toolbar for it.
            It is also the idiom the certificate page already uses for this
            exact set (its kebab opens the same rows), so a user meets one
            arrangement of Gift / Sell / Report rather than two.

            `presentation='auto'` (the default) is a dropdown anchored here at
            md+ and a bottom sheet below it — which is what a toolbar control
            should do, and is NOT what the certificate page chose
            (`presentation='sheet'`, a sheet at every width). The difference is
            deliberate: that page's kebab is the page's only route to these
            actions and wants the room; this is one control among four in a
            toolbar band.
          */}
          <ActionMenu
            items={actionItems}
            title='Actions'
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            trigger={
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{
                  px: { xs: 1, md: 2 },
                  py: 1,
                  // VRG-562: a control inside the bar — the raw brand radius, as buttons take it.
                  borderRadius: innerRadius,
                  color: 'text.primary',
                  '&:hover': { backgroundColor: theme => theme.palette.grey[100] },
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 500, color: 'inherit' }}>
                  Actions
                </Typography>
                <ChevronDown size={16} />
              </Stack>
            }
          />
        </Stack>

        <Button
          onClick={onClearSelection}
          disabled={!hasSelection}
          disableRipple
          sx={{
            textTransform: 'none',
            fontSize: 16,
            fontWeight: 500,
            color: 'text.primary',
            px: 1,
            py: 0.5,
            minWidth: 0,
            // VRG-562: a button, so the raw brand radius (see `innerPx`).
            borderRadius: innerRadius,
            '&:hover': { backgroundColor: (theme: any) => theme.palette.grey[100] },
            '&.Mui-disabled': { color: '#9ca3af' },
          }}
        >
          Reset
        </Button>
      </Stack>

      {/*
        THE RESOLVER, at both widths.

        Naming the blockers is the menu's job now — each disabled row carries its
        own reason, on the row, where a phone can read it. This is the other half:
        the reason tells you what is wrong, this fixes it. Without it the reader
        still has to find those rows in a long list and untick them by hand, which
        is the actual work the message was describing.

        It carries the COUNT because "Deselect them" read as a duplicate of the
        Reset control above — "them" had to reach back across a sentence to find
        its antecedent, so the two controls looked like two ways to undo a
        selection. They are not: Reset clears all of it, this keeps the actionable
        remainder selected and turns the menu back on. The number is what makes
        that visible without reading the code.

        It was previously mobile-only, because it hung off a mobile-only inline
        reason block. The problem it solves is not mobile-only.
      */}
      {showsDeselectBlocked && onDeselectIds && (
        <Stack sx={{ px: 1, pb: 1 }} alignItems='flex-start'>
          <Typography
            role='button'
            tabIndex={0}
            onClick={() => onDeselectIds(blockedIds)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onDeselectIds(blockedIds);
              }
            }}
            sx={{
              fontSize: font.label,
              fontWeight: 500,
              color: 'text.primary',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {blockedIds.length === 1
              ? 'Deselect the one that can’t be actioned'
              : `Deselect the ${blockedIds.length} that can’t be actioned`}
          </Typography>
        </Stack>
      )}

      {/*
        THE REAL FLOWS, not a second stub.

        Gift mounts the SAME `GiftCertificate` sheet the certificate page and the
        row kebab mount, on the one selected certificate — the availability helper
        has already established there is exactly one. Nothing about it is
        bulk-aware, and it is not pretending to be.

        Guarded on `giftTarget` because the sheet dereferences `certificate.id`
        and `certificate.product` on its first render; there is no lighter version
        of it without a record, only a crash.
      */}
      {giftTarget && (
        <DrawerPopup
          title='Gift'
          child={
            <GiftCertificate
              certificate={giftTarget}
              closeGift={() => {
                setOpenGift(false);
                // The gifted certificate is now mid-transfer, so leaving it
                // selected leaves the bar in a blocked state the user did not
                // ask for and has to clear by hand.
                onClearSelection();
              }}
            />
          }
          handleOpen={openGift}
          handleClose={() => setOpenGift(false)}
        />
      )}

      {/*
        Report takes the WHOLE selection — see `ReportMissingDrawer.handleReport`
        for why a client-side sequential fan-out is the honest shape here and why
        it must not be parallelised.

        No `onViewCertificate` / `onShare`: those name one certificate and there
        may be five. The drawer gates them on the handler existing, so omitting
        them is what removes the buttons rather than leaving two more controls
        that do nothing.
      */}
      <ReportMissingDrawer
        open={openReportMissing}
        certificateIds={selectedIds}
        /*
          The selection is cleared on CLOSE, and only if something was actually
          reported — not from `onStatusChange`.

          Clearing on `onStatusChange` was the obvious wiring and it breaks the
          two states that matter most. The drawer keeps reading `certificateIds`
          while it is open: a PARTIAL failure fires `onStatusChange` for the
          records that did land, so clearing there would empty the selection
          under a drawer still offering "Try again", and the success screen — which
          counts the run from the same prop — would announce five reports as one.
          Clearing on dismissal instead leaves the drawer's inputs stable for as
          long as it is on screen.

          The `reported` flag is what keeps a plain cancel from wiping a selection
          the user has not acted on.
        */
        onClose={() => {
          setOpenReportMissing(false);
          if (reportedRef.current) {
            reportedRef.current = false;
            onClearSelection();
          }
        }}
        onStatusChange={() => {
          reportedRef.current = true;
          onActionComplete?.();
        }}
      />
    </Box>
  );
};

export default SelectionToolbar;
